import { WebAPIAmendmentsPage } from '@/globals/webApiTypes/pages/WebAPIAmendmentsPage';
import { languageCode } from '@/globals/config/localization.config';
import { matchesSelectedFiltersByQueryKeys } from '@/sections/filter/matchesSelectedFilters';
import { SelectedFiltersMap } from '@/sections/filter/getSelectedFilters';
import { amendmentFilterQueryKeys } from './amendmendsFilter/amendmentFilterKeys';

const collator = new Intl.Collator(languageCode, { sensitivity: 'base' });

type AmendmentFilters = WebAPIAmendmentsPage['filters'];
type AmendmentResult = WebAPIAmendmentsPage['results'][number];

const amendmentResultFieldByQueryKey = {
	subject: 'subjects',
	sector: 'sectors',
} as const satisfies Record<
	keyof typeof amendmentFilterQueryKeys,
	keyof AmendmentResult
>;

const amendmentOptionsFieldByQueryKey = {
	subject: 'subjects',
	sector: 'sectors',
} as const satisfies Record<
	keyof typeof amendmentFilterQueryKeys,
	keyof AmendmentFilters
>;

const matchesSelectedFiltersConfig = {
	filterQueryKeys: amendmentFilterQueryKeys,
	resultFieldByQueryKey: amendmentResultFieldByQueryKey,
	optionsFieldByQueryKey: amendmentOptionsFieldByQueryKey,
	normalizeByQueryKey: {},
} as const;

const hasDataForSelectedFilterGroup = (
	result: AmendmentResult,
	groupKey: string,
) => {
	// Find the query key for this group
	const queryKey = Object.entries(amendmentFilterQueryKeys).find(
		([, key]) => key === groupKey,
	)?.[0] as keyof typeof amendmentResultFieldByQueryKey | undefined;

	if (!queryKey) {
		return true;
	}

	const resultField = amendmentResultFieldByQueryKey[queryKey];
	const resultValue = result[resultField] as string | Array<string> | undefined;

	// Check if result has data for this field
	if (resultValue == null) {
		return false;
	}

	if (Array.isArray(resultValue)) {
		return resultValue.some((v) => v.trim() !== '');
	}

	return resultValue.trim() !== '';
};

export const filterAmendmentResults = (
	results: Array<AmendmentResult> | undefined,
	selectedFilters: SelectedFiltersMap,
	filters: AmendmentFilters,
) => {
	// If no filters selected, show all results
	let hasAnySelectedFilters = false;
	for (const selectedValues of selectedFilters.values()) {
		if (selectedValues.size > 0) {
			hasAnySelectedFilters = true;
			break;
		}
	}

	if (!hasAnySelectedFilters) {
		const all = results ?? [];
		return all.sort((a, b) => {
			return collator.compare(a.title ?? '', b.title ?? '');
		});
	}

	const filtered = results?.filter((result) => {
		// Check if result matches ANY selected filter group (OR logic between groups)
		for (const [groupKey, selectedValues] of selectedFilters) {
			if (selectedValues.size === 0) {
				continue;
			}

			// Only check this group if result has data for it
			if (!hasDataForSelectedFilterGroup(result, groupKey)) {
				continue;
			}

			// Check if result matches this group
			const matchesThisGroup = matchesSelectedFiltersByQueryKeys({
				result,
				selectedFilters: new Map([[groupKey, selectedValues]]),
				filters,
				...matchesSelectedFiltersConfig,
			});

			// If matches any group, include result
			if (matchesThisGroup) {
				return true;
			}
		}

		return false;
	});

	return filtered?.sort((a, b) => {
		// Sort alphabetically by title
		return collator.compare(a.title ?? '', b.title ?? '');
	});
};

export const hasMatchingAmendmentResults = (
	results: Array<AmendmentResult> | undefined,
	selectedFilters: SelectedFiltersMap,
	filters: AmendmentFilters,
) => {
	// If no filters selected, all results match
	let hasAnySelectedFilters = false;
	for (const selectedValues of selectedFilters.values()) {
		if (selectedValues.size > 0) {
			hasAnySelectedFilters = true;
			break;
		}
	}

	if (!hasAnySelectedFilters) {
		return (results ?? []).length > 0;
	}

	return (results ?? []).some((result) => {
		// Check if result matches ANY selected filter group (OR logic between groups)
		for (const [groupKey, selectedValues] of selectedFilters) {
			if (selectedValues.size === 0) {
				continue;
			}

			// Only check this group if result has data for it
			if (!hasDataForSelectedFilterGroup(result, groupKey)) {
				continue;
			}

			// Check if result matches this group
			const matchesThisGroup = matchesSelectedFiltersByQueryKeys({
				result,
				selectedFilters: new Map([[groupKey, selectedValues]]),
				filters,
				...matchesSelectedFiltersConfig,
			});

			// If matches any group, return true
			if (matchesThisGroup) {
				return true;
			}
		}

		return false;
	});
};
